import {
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  Spacer,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useInfiniteQuery, useMutation } from "react-query";
import { useNavigate } from "react-router-dom";
import { FixedSizeList as List } from "react-window";
import InfiniteLoader from "react-window-infinite-loader";
import {
  addComicInReadingOrder,
  getMyReadingOrderComics,
  removeComicInReadingOrder,
  updateComicInReadingOrder,
  updateMyOrder,
} from "../../../services/readingOrdersApi";
import Spinner from "../../../shared/components/Spinner";
import MyComicRow from "../components/MyComicRow";
import MyAutocomplete from "./Autocomplete";
import { mergeRefs } from "../../../core/utils";

function MyReadingOrderComics({ readingOrder }) {
  const navigate = useNavigate();
  const toast = useToast();
  const listRef = useRef();
  const [changed, setChanged] = useState(false);
  const [lastIndexAdded, setLastIndexAdded] = useState(null);
  const [comics, setComics] = useState([]);
  const readingOrderId = readingOrder.id;
  let navbar = document.querySelector("header nav");
  let navbarHeight = navbar.offsetHeight;
  let availableHeight = window.innerHeight - navbarHeight - 200;

  const orderQuery = useInfiniteQuery(
    ["readingOrderComics", readingOrderId],
    ({ pageParam = 1 }) => getMyReadingOrderComics(readingOrderId, pageParam),
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      getNextPageParam: (pageParam, allPages) => {
        if (pageParam?.meta.currentPage === pageParam?.meta.lastPage) {
          return undefined;
        }
        return pageParam?.meta?.currentPage + 1;
      },
      getPreviousPageParam: (pageParam, pages) => {
        if (pageParam?.meta.currentPage === 1) {
          return undefined;
        }
        return pageParam?.meta?.currentPage - 1;
      },
      onSuccess: (data) => {
        // Flatten the newly fetched data with the existing data
        const newComics = data.pages.flatMap((page) => page.data);
        setComics(newComics);
      },
    }
  );
  const addComicMutation = useMutation(({ comicId, order }) => {
    return addComicInReadingOrder(readingOrderId, comicId, order);
  });

  const updateComicMutation = useMutation(({ comicId, order, story }) => {
    return updateComicInReadingOrder(readingOrderId, comicId, order, story);
  });

  const removeComicMutation = useMutation(({ comicId, order }) => {
    return removeComicInReadingOrder(readingOrderId, comicId, order);
  });

  const { mutateAsync } = useMutation(() => {
    return updateMyOrder(readingOrderId);
  });

  // Check if a new comic has been added to the end of the list, and scroll
  useEffect(() => {
    if (
      listRef.current &&
      comics.length > 0 &&
      lastIndexAdded !== null &&
      comics.length === lastIndexAdded + 1
    ) {
      // Scroll to the last item in the list
      listRef.current.scrollToItem(comics.length - 1, "end");
    }
  }, [comics.length, lastIndexAdded]);

  const isItemLoaded = (index) => {
    return (
      !orderQuery.hasNextPage ||
      index <
        orderQuery.data.pages.reduce(
          (total, page) => total + page.data.length,
          0
        )
    );
  };

  const loadMoreItems = orderQuery.isFetchingNextPage
    ? () => {}
    : () => orderQuery.fetchNextPage();

  // Handle form submit
  function handleSubmit(event) {
    event.preventDefault();
    mutateAsync({ readingOrderId }).then(() => {
      toast({
        title: "Order updated.",
        description: "Your reading order has been saved successfully.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      navigate(`/my-orders/${readingOrderId}`);
    });
  }

  const handleAddBefore = useCallback((index) => {
    if (index === 0) {
      return;
    }

    setComics((currentComics) => [
      ...currentComics.slice(0, index),
      {
        new: true,
        order:
          currentComics[index - 1].order +
          (currentComics[index].order - currentComics[index - 1].order) / 2,
      },
      ...currentComics.slice(index),
    ]);
  }, []);

  // Handle comic removal
  const handleAddAfter = useCallback((index) => {
    setComics((currentComics) => [
      ...currentComics.slice(0, index + 1),
      {
        new: true,
        order:
          currentComics[index].order +
          (currentComics[index + 1].order - currentComics[index].order) / 2,
      },
      ...currentComics.slice(index + 1),
    ]);
  }, []);

  // Handle comic removal
  const handleRemove = useCallback(
    (index, comicId, order) => {
      // CALL REMOVE
      if (comicId) {
        removeComicMutation
          .mutateAsync({
            comicId,
            order,
          })
          .then(() => {
            setComics((currentComics) =>
              currentComics.filter((_, i) => i !== index)
            );
          });
      } else {
        setComics((currentComics) =>
          currentComics.filter((_, i) => i !== index)
        );
      }
    },
    [removeComicMutation]
  );

  const handleAddOrReplace = useCallback(
    (index, comic, row, story) => {
      setComics((currentComics) => {
        const newComics = [...currentComics];
        if (index === newComics.length) {
          // Call ADD COMIC
          addComicMutation
            .mutateAsync({ comicId: comic.id, order: index + 1 })
            .then(() => {
              newComics.push({ ...comic, new: false, order: index + 1 });
            });
        } else {
          // CALL UPDATE
          if (!!row.new) {
            addComicMutation.mutateAsync({
              comicId: comic.id,
              order: row.order,
            });
          } else {
            updateComicMutation.mutate({ comicId: comic.id, order: row.order });
          }
          newComics[index] = { ...comic, order: row.order, new: false };
        }
        return newComics;
      });
      setLastIndexAdded(index);
      setChanged(true);
    },
    [addComicMutation, updateComicMutation]
  );

  const setStory = useCallback(
    (index, story, row) => {
      // Update only the specific comic in the array
      setComics((currentComics) => {
        const newComics = [...currentComics];
        newComics[index] = { ...newComics[index], story };
        return newComics;
      });
      setChanged(true);

      if (row.id) {
        // Perform the update mutation
        updateComicMutation.mutate({
          comicId: row.id,
          order: row.order,
          story,
        });
      }
    },
    [updateComicMutation]
  );

  const renderComicRow = useCallback(
    ({ index, style }) => {
      const comic = comics[index];
      return (
        <MyComicRow
          key={`${index}`}
          style={style}
          comic={comic}
          index={index}
          franchise={readingOrder.franchise}
          isLast={!comics[index + 1]}
          handleAddOrReplace={handleAddOrReplace}
          handleRemove={handleRemove}
          handleAddBefore={handleAddBefore}
          handleAddAfter={handleAddAfter}
          setStory={setStory}
        />
      );
    },
    [
      comics,
      handleAddOrReplace,
      handleRemove,
      handleAddBefore,
      handleAddAfter,
      setStory,
      readingOrder,
    ]
  );

  const itemData = useMemo(() => comics, [comics]);

  return (
    <Box>
      <Box p="3" borderBottomColor="gray.900" borderBottomWidth="1px">
        <Flex justifyContent="center">
          <Text fontSize="sm" color="gray.400" mt="2" ml="1">
            EDIT THE ORDER
          </Text>
          <Spacer />
        </Flex>
      </Box>
      <Box>
        {orderQuery.isLoading && <Spinner />}
        {orderQuery.isSuccess && !orderQuery.isLoading && (
          <form onSubmit={handleSubmit}>
            <VStack spacing={4}>
              <FormControl isRequired>
                <Box pl="3">
                  <InfiniteLoader
                    isItemLoaded={isItemLoaded}
                    itemCount={orderQuery.data?.pages[0]?.meta?.total || 5000}
                    loadMoreItems={loadMoreItems}
                  >
                    {({ onItemsRendered, ref }) => (
                      <List
                        height={
                          comics.length < 8
                            ? comics.length * 78 + 78 + 78
                            : availableHeight
                        } // Adjust based on your layout
                        itemCount={comics.length}
                        itemSize={78} // Adjust based on your row height
                        itemData={itemData}
                        width={"100%"} // Adjust as needed
                        onItemsRendered={onItemsRendered}
                        ref={mergeRefs(ref, listRef)}
                      >
                        {renderComicRow}
                      </List>
                    )}
                  </InfiniteLoader>
                </Box>
                <Box
                  display="flex"
                  alignItems="center"
                  p="3"
                  borderTopColor="gray.900"
                  borderTopWidth="1px"
                >
                  <Box pr="2">
                    <Badge bg="gray.600" borderRadius="sm" fontSize="0.8em">
                      {comics.length + 1}
                    </Badge>
                  </Box>
                  <Flex w="100%" justifyContent="center" alignItems="center">
                    <Box flex="1">
                      <MyAutocomplete
                        isLeadingInput
                        index={comics.length}
                        franchise={readingOrder.franchise}
                        onSelectionChange={handleAddOrReplace}
                        lastComic={comics[comics.length - 1]}
                      />
                    </Box>
                    {!!changed && (
                      <Box flexShrink="0" pl="3">
                        <Button
                          size="sm"
                          type="submit"
                          colorScheme="blue"
                          textColor="white"
                          onClick={(e) => handleSubmit(e)}
                          // loading={isLoading}
                          // loadingText="Updating reading order..."
                        >
                          Finish editing
                        </Button>
                      </Box>
                    )}
                  </Flex>
                </Box>
              </FormControl>
            </VStack>
          </form>
        )}
      </Box>
    </Box>
  );
}

export default MyReadingOrderComics;
